import React, { useContext, useState } from "react";
import { toast } from "react-toastify";
import Search from "../../components/form/Search";
import Button from "../../components/form/Button";
import Table from "../../components/elements/table/Table";
import {
  decodeId,
  handleFilterChange,
  removeDuplicateFromArray,
} from "../../helpers";
import DeleteModal from "../../components/common/DeleteModal";

import useFetch from "../../hooks/useFetch";
import { useDebounce } from "../../hooks/useDebounce";
import LimitSelect from "../../components/elements/LimitSelect";
import useMutation from "../../hooks/useMutation";
import Switch from "../../components/form/Switch";
import Badge from "../../components/elements/Badge";
import {
  messageList,
  messageDelete,
  messageStatusChange,
  ChatList,
  ChatMessages,
  sendMessage,
  ChatInfo,
  messageDeleteSingle,
} from "../../services/communicationService";
import dayjs from "dayjs";
import ManageMessageModal from "../../components/sections/front/communication/modals/ManageMessageModal";
import { useAuth } from "../../providers/AuthProvider";
import SocketContext, { useSocket } from "../../providers/SocketContext";
import MessageUi from "../../components/elements/messeging/MessageUi";
import { useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";

function groupByChatId(allMessages) {
  if (allMessages && Array.isArray(allMessages)) {
    const group_by_chatId = Object.groupBy(allMessages, ({ chatId }) => chatId);

    const arr = group_by_chatId
      ? Object.keys(group_by_chatId).map((key) => {
          return {
            chatId: key,
            data: group_by_chatId[key],
          };
        })
      : allMessages || [];
    return arr;
  } else {
    return [];
  }
}

const Communications = () => {
  const { session } = useSelector((state) => state?.authProvider);
  const location = useLocation();
  const navigate = useNavigate();
  const params = new URLSearchParams(location?.search);
  const id = params.get("id");

  let decode_id = decodeId(id ? atob(id || "{}") : "");

  const isAdmin = session?.user?.roleCode === "admin";
  const { socket } = useContext(SocketContext);

  const [keyword, setKeyword] = useState("");
  const search = useDebounce(keyword, 300);
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    data: null,
  });
  const [messageFilters, setMessageFilters] = useState({
    limit: 10,
    page: 1,
  });

  // left side chat list api ================>
  const {
    data: communicationData,
    error,
    isLoading,
    updateData: communicationDataUpdate,
    isFetching,
    refetch: communicationDataRefetch,
  } = useFetch({
    dependencies: ["communications", search],
    fetchFunction: async () => {
      const res = await ChatList({
        ...(search && { keyword: search }),
      });

      return (
        {
          ...res?.docs,
          data: res?.docs?.data?.map((item) => {
            return {
              ...item,
              chatId: item?.message?.chatId,
              time: dayjs(item?.message?.createdAt).format("hh:mm A"),
              seen: item?.message?.read?.some(
                (item) => item?.user === session?.user?._id
              ),
              status:
                item?.message?.read?.filter(
                  (user) => user?.user === item?.message?.to?.[0]
                )?.length > 0
                  ? "read"
                  : "unRead",
            };
          }),
        } || {}
      );
    },
  });

  // message list api =============>
  const {
    data: chatData,
    error: chatError,
    isLoading: chatLoading,
    updateData: chatUpdate,
    isFetching: chatFetching,
    refetch: chatRefetch,
  } = useFetch({
    dependencies: [
      `chat`,
      decode_id?.chatId,
      session?.user?.roleCode,
      messageFilters,
    ],
    fetchFunction: async () => {
      const res = await ChatMessages({
        ...messageFilters,
        ...decode_id,
        limit: 10,
      });

      if (res?.docs?.status === 200) {
        const flattenedOldMessages = (chatData?.data || []).flatMap((item) =>
          item.data ? item.data : [item]
        );

        const new_message_arr = res?.docs?.data?.map((item) => {
          return {
            _id: item?._id,
            chatId: item?.chatId,
            name: `${item?.user?.firstName} ${item?.user?.lastName}`,
            text: item?.parentText || item?.text,
            sendBy: item?.from,
            time: item?.createdAt,
            isMe: session?.user?._id === item?.user?._id,
            authorizedUserId: session?.user?._id,
            attachment: item?.attachment,
            senderImage: item?.user?.image,
          };
        });
        const allMessages = removeDuplicateFromArray([
          ...flattenedOldMessages,
          ...new_message_arr,
        ]);

        return {
          ...res?.docs,
          data: allMessages,
        };
      } else {
        if (chatData?.data?.length > 0) {
          return chatData;
        } else {
          return res;
        }
      }
    },
    enabled: !!decode_id?.chatId,
  });

  // reply on chat ===================>
  const {
    mutate: reply,
    error: replyError,
    isPending: replyPending,
  } = useMutation({
    mutationFn: async ({ val, uploadProgress }) => {
      delete decode_id?.senderId;
      return await sendMessage(
        {
          ...decode_id,
          parentText: val?.message,
          attachment: val?.attachment,
          chatMessage: true,
        },
        (progress) => {
          uploadProgress(progress);
        }
      );
    },
    onSuccess: (res) => {
      if (res?.status === 200) {
        socket?.emit("sendMessage", res);
        communicationDataUpdate((prev) => {
          const new_chat = {
            _id: res?.data?._id,
            chatId: res?.data?.chatId,
            message: {
              from: res?.data?.from,
              to: res?.data?.to,
              attachment:
                res?.data?.attachment === true || res?.data?.attachment?.url
                  ? true
                  : false,
              // subject: res?.data?.subject,
              chatId: res?.data?.chatId,
              parentText: res?.data?.parentText,
              text: res?.data?.text,
              createdAt: res?.data?.createdAt,
            },
            unseenMessageCount: 2,
            participants: res?.data?.toUser,
            participantsArray: [res?.data?.from, ...(res?.data?.to || [])],
            scholarshipDetails: res?.data?.scholarshipDetails,
            time: dayjs(res?.data?.createdAt).format("hh:mm A"),
            status: "unRead",
          };

          if (Array.isArray(prev?.data) && prev?.data?.length > 0) {
            if (
              prev?.data?.some((item) => item?.chatId === res?.data?.chatId)
            ) {
              const data = prev?.data?.map((item) =>
                item?.chatId === res?.data?.chatId ? new_chat : item
              );
              return {
                ...prev,
                data: data,
              };
            } else {
              return {
                ...prev,
                data: [new_chat, ...(prev?.data || [])],
              };
            }
          } else {
            return {
              ...prev,
              data: [new_chat, ...(prev?.data || [])],
            };
          }
        });
        chatUpdate((prev) => {
          try {
            const newMessage = {
              _id: res?.data?._id,
              name: `${res.data.user?.firstName || ""} ${
                res.data.user?.lastName || ""
              }`.trim(),
              chatId: res?.data?.chatId,
              text: res.data.parentText || res.data.text,
              sendBy: res.data.from,
              time: res.data.createdAt,
              isMe: session?.user?._id === res.data.user?._id,
              attachment: res.data.attachment,
              senderImage: res.data.user?.image,
              authorizedUserId: session?.user?._id,
            };

            if (!prev || !Array.isArray(prev?.data)) {
              return {
                ...prev,
                data: [newMessage],
              };
            }

            if (prev && Array.isArray(prev?.data) && prev?.data?.length === 0) {
              return {
                ...prev,
                data: [newMessage],
              };
            }
            return {
              ...prev,
              data: [...prev?.data, newMessage],
            };
          } catch (error) {
            console.error("Chat update error:", error);
            return prev;
          }
        });
      }
    },
    onError: (error) => {},
  });

  // get instant response ================>
  useSocket(
    "sendMessageResponse",
    async (res) => {
      if (res?.status !== 200 || !res?.data) return;
      if (res?.data?.to.includes(session?.user?._id)) {
        socket.emit("totalUnreadCount");
        communicationDataUpdate((prev) => {
          const new_chat = {
            _id: res?.data?._id,
            chatId: res?.data?.chatId,
            message: {
              from: res?.data?.from,
              to: res?.data?.to,
              attachment:
                res?.data?.attachment === true || res?.data?.attachment?.url
                  ? true
                  : false,
              // subject: res?.data?.subject,
              chatId: res?.data?.chatId,
              parentText: res?.data?.parentText,
              text: res?.data?.text,
              createdAt: res?.data?.createdAt,
            },
            unseenMessageCount: 2,
            participants: [res?.data?.user],
            participantsArray: [res?.data?.from, ...(res?.data?.to || [])],
            scholarshipDetails: res?.data?.scholarshipDetails,
            time: dayjs(res?.data?.createdAt).format("hh:mm A"),
            status: "unRead",
          };

          if (Array.isArray(prev?.data) && prev?.data?.length > 0) {
            if (
              prev?.data?.some((item) => item?.chatId === res?.data?.chatId)
            ) {
              const data = prev?.data?.map((item) =>
                item?.chatId === res?.data?.chatId ? new_chat : item
              );
              return {
                ...prev,
                data: data,
              };
            } else {
              return {
                ...prev,
                data: [new_chat, ...(prev?.data || [])],
              };
            }
          } else {
            return {
              ...prev,
              data: [new_chat, ...(prev?.data || [])],
            };
          }
        });

        chatUpdate((prev) => {
          try {
            const newMessage = {
              _id: res?.data?._id,
              name: `${res.data.user?.firstName || ""} ${
                res.data.user?.lastName || ""
              }`.trim(),
              chatId: res?.data?.chatId,
              text: res.data.parentText || res.data.text,
              sendBy: res.data.from,
              time: res.data.createdAt,
              isMe: session?.user?._id === res.data.user?._id,
              attachment: res.data.attachment,
              senderImage: res.data.user?.image,
              authorizedUserId: session?.user?._id,
            };

            if (!prev || !Array.isArray(prev?.data)) {
              return {
                ...prev,
                data: [
                  {
                    chatId: newMessage.chatId,
                    data: [newMessage],
                  },
                ],
              };
            }

            if (prev && Array.isArray(prev?.data) && prev?.data?.length === 0) {
              return {
                ...prev,
                data: [
                  {
                    chatId: newMessage.chatId,
                    data: [newMessage],
                  },
                ],
              };
            }
            return {
              ...prev,
              data: prev.data.map((item) =>
                item?.chatId === newMessage?.chatId
                  ? {
                      ...item,
                      data: [...item?.data, newMessage],
                    }
                  : item
              ),
            };
          } catch (error) {
            console.error("Chat update error:", error);
            return prev;
          }
        });

        communicationDataRefetch();
      }
    },
    [session?.user?._id, communicationData, chatData]
  );

  //   communications delete handler =================>
  const { mutate: deleteHandler, isPending: deletePending } = useMutation({
    mutationFn: async (payload) => {
      const res = await messageDeleteSingle({
        messageId: payload?._id,
      });

      return { payload, res };
    },
    onSuccess: ({ res, payload }) => {
      if (res?.status === 200) {
        setDeleteModal({ isOpen: false, data: null });
        socket.emit("messageDelete", { res, payload });
        chatUpdate((pre) => {
          pre.data = pre?.data?.filter(
            (item) => item?._id !== payload?._id
            // ? {
            //     ...item,
            //     data: item?.data?.filter((msg) => msg?._id !== payload?._id),
            //   }
            // : item
          );
          return { ...pre };
        });
      } else {
        toast.warning(res?.message || res?.error);
      }
    },
    onError: (error) => {
      console.log(error);
    },
  });

  // intant message delete ==============>

  useSocket(
    "messageDeleteResponse",
    async ({ res, payload }) => {
      if (res?.status !== 200) return;
      chatUpdate((pre) => {
        pre.data = pre?.data?.filter(
          (item) => item?._id !== payload?._id
          // ? {
          //     ...item,
          //     data: item?.data?.filter((msg) => msg?._id !== payload?._id),
          //   }
          // : item
        );
        return { ...pre };
      });
      communicationDataRefetch();
    },
    [session?.user?._id, chatData]
  );

  async function loadData(e, obj, setData = () => {}) {
    if (Math.ceil(e.target.scrollTop - 35) <= 0 && obj?.metadata?.hasNextPage) {
      setData((pre) => ({
        ...pre,
        page: obj?.metadata?.nextPage,
      }));
      return await ChatMessages({
        ...decode_id,
        limit: 10,
        page: obj?.metadata?.nextPage,
      });
    }
  }

  function newChatOpen(item) {
    socket.emit("messageSeenByChatId", { chatId: item?.message?.chatId });
    chatUpdate(null);
    setMessageFilters({ limit: 10, page: 1 });
  }

  useSocket(
    "messageSeenByChatIdResponse",
    (res) => {
      socket.emit("totalUnreadCount");
      communicationDataRefetch();
    },
    []
  );

  const { data: chatInfoData } = useFetch({
    dependencies: ["chatInfo", decode_id?.chatId, decode_id?.senderId],
    fetchFunction: async () => {
      const res = await ChatInfo({
        chatId: decode_id?.chatId,
        senderId: decode_id?.senderId,
      });
      return { ...res, data: { ...res?.data, chatId: decode_id?.chatId } };
    },
    enabled: !!decode_id?.chatId && !!decode_id?.senderId,
  });

  return (
    <div className="h-screen communication">
      <MessageUi
        search={setKeyword}
        sendMessageHandler={reply}
        chatList={communicationData?.data || []}
        chatData={groupByChatId(chatData?.data)
          ?.map((item) => item?.data)
          ?.flat(Infinity)}
        chatInfo={chatInfoData?.data}
        getChatInfo={(info) => newChatOpen(info)}
        onMessageScroll={async (e) => {
          return await loadData(e, chatData, setMessageFilters);
        }}
        activeChat={decode_id?.chatId}
        meassageLoading={chatLoading}
        meassageFetching={chatFetching}
        onDeleteMessage={(val) =>
          setDeleteModal((pre) => ({ data: val, isOpen: true }))
        }
      />
      <DeleteModal
        open={deleteModal?.isOpen}
        loading={deletePending}
        onClose={() => setDeleteModal((pre) => ({ data: null, isOpen: false }))}
        deleteHandler={() => {
          if (!isAdmin) {
            return;
          }
          deleteHandler(deleteModal?.data);
        }}
      />
    </div>
  );
};

export default Communications;
